import type { Auth0VueClient } from "@auth0/auth0-vue";
import { inject, type App, type Plugin } from "vue";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  Observable,
  type NormalizedCacheObject,
  type ApolloClientOptions,
  type InMemoryCacheConfig,
} from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { DefaultApolloClient, ApolloClients } from "@vue/apollo-composable";
import { createAuthLink, type AuthOptions } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";

export type { ApiConfig, MultiApiConfig, AppSyncApiConfig, OtherApiConfig };
export { createAppSyncPlugin, useAppSyncClient };

type AppSyncApiConfig = {
  endpoint: string;
  authOptions?: AuthOptions;
  region?: string;
  apolloClientOptions?: Omit<
    ApolloClientOptions<NormalizedCacheObject>,
    "link" | "cache"
  >;
  inMemoryCacheConfig?: InMemoryCacheConfig;
};

type OtherApiConfig = {
  client: ApolloClient<NormalizedCacheObject>;
};

type ApiConfig = AppSyncApiConfig | OtherApiConfig;
type MultiApiConfig = ApiConfig & { clientId: string };

const defaultRegion = "ap-northeast-1";

const defaultApolloClientOptions: AppSyncApiConfig["apolloClientOptions"] = {
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
    mutate: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  },
};

function createErrorLink(auth0: Auth0VueClient) {
  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      });
    if (networkError) {
      console.error(`[Network error]: ${networkError}`);
      const status = "statusCode" in networkError ? networkError.statusCode : 0;
      // AppSync returned 401 — the token is invalid (e.g. debug poison token,
      // or a race where the token expired between jwtToken() and the request).
      // Redirect to login and return an Observable that never emits so Apollo
      // never propagates an error to the UI. The browser navigation discards
      // the pending Observable automatically.
      if (status === 401) {
        auth0.loginWithRedirect();
        return new Observable(() => {});
      }
    }
  });
}

/**
 * Create an Apollo/AppSync Vue plugin.
 * Must be registered after the Auth0 plugin when using OIDC auth.
 *
 * @example
 * // Single API
 * app.use(createAppSyncPlugin({ endpoint: "https://..." }));
 *
 * // Multiple APIs
 * app.use(createAppSyncPlugin([
 *   { clientId: "main", endpoint: "https://..." },
 *   { clientId: "other", endpoint: "https://..." },
 * ]));
 */
function createAppSyncPlugin(api: ApiConfig): Plugin;
function createAppSyncPlugin(apis: MultiApiConfig[]): Plugin;
function createAppSyncPlugin(arg: ApiConfig | MultiApiConfig[]): Plugin {
  return {
    install: (app) => {
      if (!Array.isArray(arg)) {
        const client = createApolloClient(app, arg);
        app.provide(DefaultApolloClient, client);
        app.config.globalProperties["$appSyncClient"] = client;
      } else {
        const clients: Record<string, ApolloClient<NormalizedCacheObject>> = {};
        arg.forEach((api) => {
          clients[api.clientId] = createApolloClient(app, api);
        });
        app.provide(ApolloClients, clients);
        app.config.globalProperties["$appSyncClient"] = clients;
      }
    },
  };
}

/**
 * Inject the Apollo client (or a named client) inside a Vue component.
 */
function useAppSyncClient(
  clientId?: string
): ApolloClient<NormalizedCacheObject> {
  if (clientId) {
    const clients = inject(ApolloClients) as Record<
      string,
      ApolloClient<NormalizedCacheObject>
    >;
    return clients[clientId];
  }
  return inject(DefaultApolloClient) as ApolloClient<NormalizedCacheObject>;
}

function createApolloClient(app: App, apiConfig: ApiConfig) {
  if ("client" in apiConfig) return apiConfig.client;
  const auth0 = app.config.globalProperties["$auth0"] as Auth0VueClient;
  return createApolloClientForAppSync(
    apiConfig.endpoint,
    apiConfig.region ?? defaultRegion,
    apiConfig.authOptions ?? createOIDCAuthOptionsWithAuth0(auth0),
    auth0,
    { ...defaultApolloClientOptions, ...apiConfig.apolloClientOptions },
    apiConfig.inMemoryCacheConfig
  );
}

function createOIDCAuthOptionsWithAuth0(auth0: Auth0VueClient): AuthOptions {
  return {
    type: "OPENID_CONNECT",
    jwtToken: async () => {
      try {
        await auth0.getAccessTokenSilently();
      } catch {
        // Session expired — redirect to login and return a Promise that never
        // resolves so Apollo never fires the request. The pending Promise is
        // discarded automatically when the browser navigates to the login page.
        // This works for both imperative (Dahli) and composable (Spectee-Pro)
        // call patterns — no error reaches the UI, no retry loop, no blank screen.
        auth0.loginWithRedirect();
        return new Promise(() => {});
      }

      return auth0.idTokenClaims?.value?.__raw ?? "";
    },
  };
}

function createApolloClientForAppSync(
  url: string,
  region: string,
  auth: AuthOptions,
  auth0: Auth0VueClient,
  apolloClientOptions?: AppSyncApiConfig["apolloClientOptions"],
  inMemoryCacheConfig?: InMemoryCacheConfig
): ApolloClient<NormalizedCacheObject> {
  const httpLink = createAuthLink({ url, region, auth });
  const wsLink = createSubscriptionHandshakeLink({ url, region, auth });
  const concatLink = ApolloLink.from([
    createErrorLink(auth0),
    httpLink,
    wsLink,
  ] as unknown as ApolloLink[]);

  return new ApolloClient({
    link: concatLink,
    cache: new InMemoryCache(inMemoryCacheConfig),
    ...apolloClientOptions,
  });
}
